import React, { useEffect, useState } from 'react';
import { ConfigProvider } from 'antd';
import { Client, Provider, cacheExchange, fetchExchange } from 'urql';
import { AuthConfig, authExchange } from '@urql/exchange-auth';
import { pipe, map } from 'wonka';
import { Routes, Route } from 'react-router-dom';

import Public from "./scenes/Public/Public";
import Private from './scenes/Private/Private';
import OAuthWrapper from 'OAuthWrapper';
import { sentryExchange } from 'sentry/graphql/exchange';
import SentryErrorBoundary from './sentry/components/ErrorBoundary/SentryErrorBoundary';
import { globalErrorHandler } from './sentry/globalErrorHandler';
import {
  API_URL_GRAPHQL,
  BORROWER_ONBOARDING_URL,
  BORROWER_DASHBOARD_URL,
} from '@common/config/environments';
import { sentryInit } from 'sentry/main';
import { useNetworkStatus } from '@common/utils/hooks/useNetworkStatus';
import ModalBasic from 'components/Organisms/Modals/ModalBasic/ModalBasic';
import { multipartFetch } from './utils/multipartFetch';
import Onboarding from 'scenes/Onboarding/Onboarding';

const getToken = () => sessionStorage.getItem('token') || localStorage.getItem('token');

function App() {
  const { showModal, setShowModal } = useNetworkStatus();

  sentryInit();
  
  useEffect(() => {
    globalErrorHandler.initialize();
  }, []);

  useEffect(() => {
    const isImpersonating = sessionStorage.getItem('fromOAuth') === 'true';
    if (isImpersonating) return;

    const hasSession = document.cookie
      .split(';')
      .some(c => c.trim().startsWith('session_active='));

    const hasToken = getToken();

    if (hasToken && !hasSession) {
      console.warn('⚠️ Session invalid (no session cookie) - forcing logout');
      logout();
    }
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('fromOAuth');
    window.dispatchEvent(new Event("storage"));
    window.location.href = '/login';
  }

  // Custom exchange that injects multipartFetch for file uploads
  const multipartFetchExchange = ({ forward }: any) => {
    return (ops$: any) =>
      pipe(
        ops$,
        map((operation: any) => {
          return {
            ...operation,
            context: {
              ...operation.context,
              fetchOptions: {
                ...operation.context?.fetchOptions,
                fetch: multipartFetch,
              },
            },
          };
        }),
        forward
      );
  };

  const client = new Client({
    url: `${API_URL_GRAPHQL}/graphql`,
    fetchOptions: {
      credentials: 'include'
    },
    exchanges: [
      cacheExchange,
      sentryExchange,
      authExchange(async (utils: any) => {
        const config: AuthConfig = {
          addAuthToOperation(operation) {
            const token = getToken();
            if (!token) return operation;
            return utils.appendHeaders(operation, {
              Authorization: `Bearer ${token}`,
              'Apollo-Require-Preflight': 'true'
            });
          },
          didAuthError(error, _operation) {
            return error.response?.status === 401
          },
          async refreshAuth() { logout() },
          willAuthError(_operation) {
            return false
          },
        }

        return config;
      }),
      multipartFetchExchange,
      fetchExchange
    ]
  });

  const [isLogged, setIsLogged] = useState<boolean>(
    () => !!getToken()
  );

  // Detect which host / base URL is being used to serve this app.
  // This lets us decide whether this instance should behave as "Onboarding"
  // or as "Dashboard" using the configured cross-app URLs.
  const currentHref =
    typeof window !== 'undefined' ? window.location.href : '';

  const isOnboardingHost =
    !!BORROWER_ONBOARDING_URL &&
    currentHref.startsWith(BORROWER_ONBOARDING_URL);

  const isDashboardHost =
    !!BORROWER_DASHBOARD_URL &&
    currentHref.startsWith(BORROWER_DASHBOARD_URL);

  useEffect(() => {
    setIsLogged(!!getToken());

    window.addEventListener("storage", () => {
      setIsLogged(!!getToken());
    });

    return () => {
      window.removeEventListener("storage", () => { });
    };
  }, [])

  return (
    <SentryErrorBoundary>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#ffffff',
            colorLink: '#5E8EB7',
            colorBgContainer: '#ffffff',
          },
        }}
      >
        <ModalBasic
          visible={showModal}
          setVisible={setShowModal}
          title="A network error has occurred"
          description="Please make sure your are connected to the internet and try again."
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M10.2898 3.85996L1.81978 18C1.64514 18.3024 1.55274 18.6453 1.55177 18.9945C1.55079 19.3437 1.64127 19.6871 1.8142 19.9905C1.98714 20.2939 2.2365 20.5467 2.53748 20.7238C2.83847 20.9009 3.18058 20.9961 3.52978 21H20.4698C20.819 20.9961 21.1611 20.9009 21.4621 20.7238C21.7631 20.5467 22.0124 20.2939 22.1854 19.9905C22.3583 19.6871 22.4488 19.3437 22.4478 18.9945C22.4468 18.6453 22.3544 18.3024 22.1798 18L13.7098 3.85996C13.5315 3.56607 13.2805 3.32308 12.981 3.15444C12.6814 2.98581 12.3435 2.89722 11.9998 2.89722C11.656 2.89722 11.3181 2.98581 11.0186 3.15444C10.7191 3.32308 10.468 3.56607 10.2898 3.85996Z" stroke="#A31C17" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 9V13" stroke="#A31C17" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 17H12.01" stroke="#A31C17" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          submit={{ buttonLabel: "Return to previous screen", onSubmit: setShowModal}}
        />
        <Provider value={client}>
          <OAuthWrapper>
            <Routes>
              {/* When this app is served from the Onboarding URL, route everything to Onboarding */}
              {isOnboardingHost && (
                <Route path="/*" element={<Onboarding />} />
              )}

              {/* When this app is served from the Dashboard URL, route to Private/Public */}
              {isDashboardHost && isLogged && (
                <Route path="/*" element={<Private />} />
              )}
              {isDashboardHost && !isLogged && (
                <Route path="/*" element={<Public />} />
              )}

              {/* Fallback: keep previous behavior for non-mapped hosts (e.g. local dev) */}
              {!isOnboardingHost && !isDashboardHost && (
                <>
                  <Route path="/" element={<Onboarding />} />
                  {isLogged && <Route path="/*" element={<Private />} />}
                  {!isLogged && <Route path="/*" element={<Public />} />}
                </>
              )}
            </Routes>
          </OAuthWrapper>
        </Provider>
      </ConfigProvider>
    </SentryErrorBoundary>
  )
}

export default App;
